import React, { PureComponent } from 'react';
import { withRouter } from 'react-router';
import { Prompt } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Box, Button, Typography } from '@mic3/platform-ui';

import { bind } from 'app/utils/decorators/decoratorUtils';
import history from 'store/History';

import ModalDialog from '../ModalDialog/ModalDialog';

const TypographyStyled = styled(Typography)`
    color: ${({ theme }) => theme.base.textColor};
`;

class PromptModal extends PureComponent {
    static propTypes = {
        title: PropTypes.string,
        subTitle: PropTypes.string,
        saveText: PropTypes.string,
        discardText: PropTypes.string,
        when: PropTypes.bool,
        onSave: PropTypes.func,
    };

    static defaultProps = {
        title: 'Save changes',
        content: (
            <TypographyStyled>
                If you leave before saving your changes will be lost.
                <br />
                Do you want to save the changes?
            </TypographyStyled>
        ),
        saveText: 'Save',
        discardText: 'Discard changes',
    };

    state = {
        showPrompt: false,
        lastLocation: null,
        originalLocation: null,
        confirmedNavigation: false,
    };

    componentDidUpdate(prevProps, prevState) {
        if (!prevProps.when && this.props.when) {
            this.setState({ confirmedNavigation: false });
        }
    }

    @bind
    handleBlockedNavigation(nextLocation) {
        const { confirmedNavigation } = this.state;
        const { when, location } = this.props;

        if (confirmedNavigation) {
            return true;
        }

        if (when) {
            this.setState({
                showPrompt: true,
                lastLocation: nextLocation,
                originalLocation: {
                    pathname: location.pathname,
                    search: location.search,
                    hash: location.hash,
                },
            });
            return false;
        }

        return true;
    }

    navigateToLocation(location) {
        if (!location) return;

        const pathname = location.pathname || '';
        const search = location.search || '';
        const locationHash = location.hash || '';
        const fullPath = pathname + search + locationHash;

        if (!fullPath) return;

        const currentHash = window.location.hash.replace('#', '');

        if (currentHash === fullPath) {
            // HashRouter changes URL before Prompt can block - workaround
            const { originalLocation } = this.state;
            if (originalLocation) {
                const originalPath = originalLocation.pathname + 
                    (originalLocation.search || '') + 
                    (originalLocation.hash || '');
                
                history.replace(originalPath);
                setTimeout(() => {
                    history.push(fullPath);
                }, 10);
            } else {
                window.location.reload();
            }
        } else {
            history.push(fullPath);
        }
    }

    @bind
    handleDiscard() {
        const { lastLocation } = this.state;

        this.setState(
            {
                showPrompt: false,
                confirmedNavigation: true,
            },
            () => {
                this.navigateToLocation(lastLocation);
            }
        );
    }

    @bind
    handleSave() {
        const { onSave } = this.props;
        const { lastLocation } = this.state;

        const navigation = () => {
            this.navigateToLocation(lastLocation);
        };

        this.setState(
            {
                showPrompt: false,
                confirmedNavigation: true,
            },
            () => {
                if (onSave) {
                    onSave(null, navigation);
                } else {
                    navigation();
                }
            }
        );
    }

    render() {
        const { showPrompt } = this.state;
        const { title, content, saveText, discardText, when, location } = this.props;
        const isEmbedMode = location && location.pathname.startsWith('/embed');

        return (
            <>
                <Prompt when={when && !isEmbedMode} message={this.handleBlockedNavigation} />
                {showPrompt && (
                    <ModalDialog
                        title={title}
                        withoutClose
                        withoutFooterDivider
                        maxWidth="sm"
                        footer={
                            <Box mt={4} display="flex" justifyContent="flex-end">
                                <Button variant="text" onClick={this.handleDiscard}>
                                    {discardText}
                                </Button>
                                <Button variant="text" onClick={this.handleSave}>
                                    {saveText}
                                </Button>
                            </Box>
                        }
                    >
                        <Box mt={4}>{content}</Box>
                    </ModalDialog>
                )}
            </>
        );
    }
}

export default withRouter(PromptModal);
